/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
/**
 * v77.99.7 — Connections & Access page (parent-facing).
 *
 * Strict per-child scope: shows ONLY co-parents on children the user
 * shares. Backend `/api/connections` enforces the access rules; this
 * page is a read mostly-view with two safe actions (resend invite,
 * revoke connection) that re-use the existing per-child endpoints.
 *
 * Design intent: clarity + reassurance. Not a social directory.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, ChevronLeft, BadgeCheck, ShieldAlert, Mail, Crown,
  Repeat, AlertTriangle, MessageCircle, RefreshCw,
} from "lucide-react";


export default function ConnectionsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revoke, setRevoke] = useState(null); // {user_id, child_id, name, scope}
  const [report, setReport] = useState(null); // {name}

  const load = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.get("/connections");
      setData(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load your connections");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleResend = async (inv) => {
    if (!window.confirm(`Resend the invite for ${inv.email_masked}?`)) return;
    try {
      await api.post(`/children/${inv.child_id_for_actions}/invites/${inv.invite_id}/resend`);
      toast.success("Invite re-sent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  const handleRevokeInvite = async (inv) => {
    if (!window.confirm(`Cancel the invite for ${inv.email_masked}? They won't be able to use it any more.`)) return;
    try {
      await api.post(`/children/${inv.child_id_for_actions}/invites/${inv.invite_id}/revoke`);
      toast.success("Invite cancelled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  return (
    <div className="px-4 sm:px-6 py-6 max-w-4xl mx-auto" data-testid="connections-page">
      <Link
        to="/app/dashboard"
        className="inline-flex items-center gap-1 text-[12px] text-[#6F828A] hover:text-[#0B2E32] mb-4"
        data-testid="connections-back"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> Back to dashboard
      </Link>

      <div className="mb-5">
        <h1 className="font-heading text-[#0B2E32] text-[24px] font-semibold tracking-tight inline-flex items-center gap-2">
          <Users className="w-5 h-5 text-[#0F4F52]" /> Connections &amp; Access
        </h1>
        <p className="text-[13px] text-[#4A5560] mt-1.5 leading-relaxed">
          A clear view of every co-parent and viewer connected to children you can access.
          For your privacy, you'll only see co-parents on children you share — never anyone outside your scope.
        </p>
      </div>

      {data?.scope_note && (
        <div
          className="text-[12px] text-[#4A5560] bg-[#EEF1F2] border border-[#D9E1E3] rounded-sm px-3 py-2 mb-5"
          data-testid="connections-scope-note"
        >
          {data.scope_note}
        </div>
      )}

      {/* Connected co-parents */}
      <section className="mb-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[14px] font-semibold text-[#0B2E32] uppercase tracking-[0.08em]">
            Connected co-parents
          </h2>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            className="text-[12px] text-[#1F9FA4] hover:text-[#0F4F52] inline-flex items-center gap-1"
            data-testid="connections-reload"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        {busy && !data ? (
          <div className="text-sm text-[#6F828A] py-8 text-center">Loading…</div>
        ) : !data || data.connected_coparents.length === 0 ? (
          <div className="rounded-md border border-[#D9E1E3] bg-white px-5 py-8 text-center">
            <p className="text-[13px] text-[#4A5560]">
              No connected co-parents yet. Open a child's profile to invite a co-parent to share access.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="connections-list">
            {data.connected_coparents.map((cp) => (
              <CoparentCard
                key={cp.user_id}
                cp={cp}
                onRevoke={() => setRevoke(cp)}
                onReport={() => setReport(cp)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      <section>
        <h2 className="text-[14px] font-semibold text-[#0B2E32] uppercase tracking-[0.08em] mb-3">
          Pending invites
        </h2>
        {!data || data.pending_invites.length === 0 ? (
          <div className="rounded-md border border-[#D9E1E3] bg-white px-5 py-6 text-center">
            <p className="text-[13px] text-[#4A5560]">
              No pending invites you've sent are awaiting acceptance.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="pending-invites-list">
            {data.pending_invites.map((inv) => (
              <InviteCard
                key={inv.invite_id}
                inv={inv}
                onResend={() => handleResend(inv)}
                onRevoke={() => handleRevokeInvite(inv)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Revoke confirmation modal */}
      <RevokeConnectionDialog
        cp={revoke}
        onClose={() => setRevoke(null)}
        onConfirmed={async () => {
          if (!revoke) return;
          try {
            await api.delete(`/children/${revoke.actions.child_id_for_actions}/coparents/${revoke.user_id}`);
            toast.success(`${revoke.name} removed from this child`);
            setRevoke(null);
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Revoke failed");
          }
        }}
      />

      {/* Report issue modal */}
      <ReportIssueDialog
        cp={report}
        onClose={() => setReport(null)}
      />
    </div>
  );
}


/* ---------- Single co-parent card ---------- */
function CoparentCard({ cp, onRevoke, onReport }) {
  return (
    <li
      className="rounded-md border border-[#D9E1E3] bg-white p-4 shadow-none"
      data-testid={`connection-${cp.user_id}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div
          className="w-9 h-9 rounded-full bg-[#0F4F52] text-white inline-flex items-center justify-center text-[13px] font-semibold flex-shrink-0"
          aria-hidden="true"
        >
          {(cp.name || "?").trim().charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[14.5px] font-semibold text-[#0B2E32] truncate">{cp.name}</span>
            {cp.is_implicit_primary && (
              <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border border-[#C5B2DE] bg-[#ECE3F3] text-[#4E3877] inline-flex items-center gap-1">
                <Crown className="w-3 h-3" /> Managing parent
              </span>
            )}
            <StatusBadge status={cp.invite_status} />
            <VerificationBadge status={cp.verification_status} />
          </div>
          <div className="text-[12.5px] text-[#4A5560] mt-1 inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> <span className="font-mono-data">{cp.email_masked || "(no email)"}</span>
          </div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[12.5px]">
            <Field label="Children shared" value={cp.access_scope || "—"} />
            <Field label="Date connected" value={cp.date_connected ? formatDT(cp.date_connected) : "—"} />
            <Field label="Access scope" value={`${cp.shared_children.length} child${cp.shared_children.length === 1 ? "" : "ren"}`} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {cp.actions.can_revoke && (
              <Button
                type="button"
                variant="outline"
                onClick={onRevoke}
                className="h-8 text-[12px] border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
                data-testid={`connection-revoke-${cp.user_id}`}
              >
                Remove access
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={onReport}
              className="h-8 text-[12px] border-[#D9E1E3] text-[#4A5560] hover:bg-[#F8FAFB] rounded-sm"
              data-testid={`connection-report-${cp.user_id}`}
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> Report issue
            </Button>
          </div>
        </div>
      </div>
    </li>
  );
}


/* ---------- Invite card ---------- */
function InviteCard({ inv, onResend, onRevoke }) {
  const childNames = inv.shared_children.map((c) => c.name).join(", ");
  return (
    <li
      className="rounded-md border border-[#E6CFA2] bg-[#FDFBF6] p-4"
      data-testid={`pending-invite-${inv.invite_id}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-[14px] font-semibold text-[#0B2E32]">{inv.email_masked || "(unknown email)"}</span>
        <StatusBadge status={inv.status} />
      </div>
      <div className="text-[12.5px] text-[#4A5560] mt-1">
        For: <span className="font-medium text-[#0B2E32]">{childNames}</span>
        {inv.created_at && (<> · sent {formatDT(inv.created_at)}</>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {inv.can_resend && (
          <Button
            type="button"
            variant="outline"
            onClick={onResend}
            className="h-8 text-[12px] border-[#1F9FA4] text-[#0F4F52] hover:bg-[#E0F2F2] rounded-sm"
            data-testid={`invite-resend-${inv.invite_id}`}
          >
            <Repeat className="w-3 h-3 mr-1" /> Resend invite
          </Button>
        )}
        {inv.can_revoke && (
          <Button
            type="button"
            variant="outline"
            onClick={onRevoke}
            className="h-8 text-[12px] border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
            data-testid={`invite-revoke-${inv.invite_id}`}
          >
            Cancel invite
          </Button>
        )}
      </div>
    </li>
  );
}


/* ---------- Helpers ---------- */
function Field({ label, value }) {
  return (
    <div>
      <span className="text-[10px] uppercase tracking-[0.08em] text-[#4A5560]">{label}</span>
      <div className="text-[#0B2E32]">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tone = {
    accepted: "bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9]",
    pending:  "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]",
    expired:  "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]",
  }[status] || "bg-[#EEF1F2] text-[#4A5560] border-[#D9E1E3]";
  return (
    <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${tone}`}>
      {status || "unknown"}
    </span>
  );
}

function VerificationBadge({ status }) {
  if (status === "verified") {
    return (
      <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9] inline-flex items-center gap-1">
        <BadgeCheck className="w-3 h-3" /> Verified
      </span>
    );
  }
  return (
    <span
      className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2] inline-flex items-center gap-1"
      title="This co-parent has not yet verified their email address."
    >
      <ShieldAlert className="w-3 h-3" /> Email unverified
    </span>
  );
}


/* ---------- Modals ---------- */
function RevokeConnectionDialog({ cp, onClose, onConfirmed }) {
  const [confirmText, setConfirmText] = useState("");
  useEffect(() => { setConfirmText(""); }, [cp?.user_id]);
  const required = cp?.name || "";
  const matches = confirmText.trim().toLowerCase() === required.trim().toLowerCase();
  return (
    <Dialog open={!!cp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="revoke-connection-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#8C2A22] inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Remove this co-parent's access?
          </DialogTitle>
          <DialogDescription className="text-[#4A5560] text-[13px] mt-2 leading-relaxed">
            <span className="block">
              <strong className="text-[#0B2E32]">{cp?.name}</strong> will lose access to{" "}
              <strong className="text-[#0B2E32]">{cp?.access_scope}</strong> immediately.
              They'll no longer be able to view messages, expenses, calendar entries
              or journal notes for {cp?.shared_children?.length === 1 ? "this child" : "these children"}.
            </span>
            <span className="block mt-2">
              This action is reversible — you can re-invite them later — but it ends their current session.
              To confirm, type the co-parent's name below:
            </span>
          </DialogDescription>
        </DialogHeader>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={required}
          className="w-full border border-[#D9E1E3] rounded-sm h-9 px-3 text-[14px] text-[#0B2E32]"
          data-testid="revoke-confirm-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm h-9 text-[13px]">Cancel</Button>
          <Button
            disabled={!matches}
            onClick={onConfirmed}
            className="bg-[#8C2A22] hover:bg-[#6E2018] text-white rounded-sm h-9 text-[13px]"
            data-testid="revoke-confirm-button"
          >
            Remove access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportIssueDialog({ cp, onClose }) {
  return (
    <Dialog open={!!cp} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="report-issue-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0B2E32] inline-flex items-center gap-2">
            <MessageCircle className="w-4 h-4" /> Report an issue
          </DialogTitle>
          <DialogDescription className="text-[#4A5560] text-[13px] mt-2 leading-relaxed">
            If you believe <strong className="text-[#0B2E32]">{cp?.name}</strong> shouldn't have access,
            or you have a safeguarding or harassment concern, our support team can review the connection
            and act on it directly.
          </DialogDescription>
        </DialogHeader>
        <div className="text-[13px] text-[#0B2E32] space-y-2">
          <p>
            Please email <a className="text-[#1F9FA4] hover:underline" href="mailto:support@coparentconsole.com?subject=Access%20review%20request">support@coparentconsole.com</a> with:
          </p>
          <ul className="list-disc pl-5 text-[#4A5560] space-y-1">
            <li>The co-parent's name (already prefilled below for you to copy).</li>
            <li>Which child or children are affected.</li>
            <li>A short description of the concern.</li>
          </ul>
          <pre className="bg-[#F8FAFB] border border-[#D9E1E3] rounded-sm p-2 text-[11px] mt-2 whitespace-pre-wrap" data-testid="report-issue-prefill">
{`Co-parent: ${cp?.name || ""}
Children affected: ${cp?.access_scope || ""}
Concern: `}</pre>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="rounded-sm h-9 text-[13px] bg-[#0F4F52] hover:bg-[#0B3F41] text-white">Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
